import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  X,
  User,
  Mail,
  Phone,
  Building,
  Globe,
  Users,
  MessageSquare,
  Plus,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { addLead, getLeads } from "../../apiHelper";

const NewLeadModal = (props) => {
  const { open, setOpen, setLeads } = props;
  const [formData, setFormData] = useState({
    leadName: "",
    phoneNumber: "",
    emailAddress: "",
    contactPersonFirstName: "",
    contactPersonLastName: "",
    companySize: "",
    industry: "",
    jobTitle: "",
    website: "",
    howDidYouHear: "",
    specificNeeds: "",
    channel: "website",
  });
  const [submitClicked, setSubmitClicked] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessTokenSilently } = useAuth();
  const handleClick = async () => {
    setSubmitClicked(true);
    setIsLoading(true);
    getAccessTokenSilently().then((t) => {
      addLead(t, formData)
        .then(() => {
          setFormData({
            leadName: "",
            phoneNumber: "",
            emailAddress: "",
            contactPersonFirstName: "",
            contactPersonLastName: "",
            companySize: "",
            industry: "",
            jobTitle: "",
            website: "",
            howDidYouHear: "",
            specificNeeds: "",
            channel: "website",
          });
          getLeads(t, { page: 1 }).then((newResponse) => {
            setLeads(newResponse);
          });
          setOpen(false);
          setSubmitClicked(false);
          setIsLoading(false);
        })
        .catch((e) => {
          setSubmissionMessage(
            JSON.parse(e.response?.request?.response).message
          );
          setTimeout(() => setSubmissionMessage(null), 2000);
          setIsLoading(false);
        });
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white/95 backdrop-blur-sm border-0 shadow-2xl shadow-gray-200/50 rounded-2xl">
        <DialogHeader className="pb-6">
          <DialogTitle className="flex items-center gap-3 text-2xl font-bold text-gray-900">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Plus className="h-5 w-5 text-white" />
            </div>
            Add New Lead
          </DialogTitle>
        </DialogHeader>

        {submissionMessage && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200">
            <div className="text-red-800 font-medium">{submissionMessage}</div>
          </div>
        )}

        <div className="space-y-8">
          {/* Basic Information */}
          <Card className="bg-white/60 backdrop-blur-sm border-0 shadow-lg shadow-gray-200/30 rounded-2xl">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-lg font-bold text-gray-900">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                  <User className="h-4 w-4 text-white" />
                </div>
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Company Name *
                  </label>
                  <Input
                    value={formData.leadName}
                    onChange={(e) =>
                      setFormData({ ...formData, leadName: e.target.value })
                    }
                    placeholder="e.g., Ardorio"
                    className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                    error={submitClicked && !formData.leadName}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Email Address *
                  </label>
                  <Input
                    value={formData.emailAddress}
                    onChange={(e) =>
                      setFormData({ ...formData, emailAddress: e.target.value })
                    }
                    placeholder="e.g., ryan@ardorio.co"
                    className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                    error={submitClicked && !formData.emailAddress}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <Input
                    value={formData.contactPersonFirstName}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contactPersonFirstName: e.target.value,
                      })
                    }
                    placeholder="e.g., Ryan"
                    className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                    error={submitClicked && !formData.contactPersonFirstName}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <Input
                    value={formData.contactPersonLastName}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        contactPersonLastName: e.target.value,
                      })
                    }
                    placeholder="e.g., Kwan"
                    className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                    error={submitClicked && !formData.contactPersonLastName}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <Input
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, phoneNumber: e.target.value })
                    }
                    placeholder="e.g., +64 27 266 8144"
                    className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Channel
                  </label>
                  <Select
                    value={formData.channel}
                    onValueChange={(value) =>
                      setFormData({ ...formData, channel: value })
                    }
                  >
                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200">
                      <SelectValue placeholder="Select channel" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl border-0 shadow-xl">
                      <SelectItem value="website">Website</SelectItem>
                      <SelectItem value="outbound">Outbound</SelectItem>
                      <SelectItem value="demo">Demo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Details */}
          <Card className="bg-white/60 backdrop-blur-sm border-0 shadow-lg shadow-gray-200/30 rounded-2xl">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-lg font-bold text-gray-900">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <Building className="h-4 w-4 text-white" />
                </div>
                Company Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Company Size
                  </label>
                  <Select
                    value={formData.companySize}
                    onValueChange={(value) =>
                      setFormData({ ...formData, companySize: value })
                    }
                  >
                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200">
                      <SelectValue placeholder="Select company size" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl border-0 shadow-xl">
                      <SelectItem value="1-10">1-10 employees</SelectItem>
                      <SelectItem value="11-50">11-50 employees</SelectItem>
                      <SelectItem value="51-200">51-200 employees</SelectItem>
                      <SelectItem value="201-500">201-500 employees</SelectItem>
                      <SelectItem value="500+">500+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Industry
                  </label>
                  <Input
                    value={formData.industry}
                    onChange={(e) =>
                      setFormData({ ...formData, industry: e.target.value })
                    }
                    placeholder="e.g., Healthcare, Technology"
                    className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Job Title
                  </label>
                  <Input
                    value={formData.jobTitle}
                    onChange={(e) =>
                      setFormData({ ...formData, jobTitle: e.target.value })
                    }
                    placeholder="e.g., HR Manager, CEO"
                    className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Website
                  </label>
                  <Input
                    value={formData.website}
                    onChange={(e) =>
                      setFormData({ ...formData, website: e.target.value })
                    }
                    placeholder="e.g., https://company.com"
                    className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card className="bg-white/60 backdrop-blur-sm border-0 shadow-lg shadow-gray-200/30 rounded-2xl">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3 text-lg font-bold text-gray-900">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/25">
                  <MessageSquare className="h-4 w-4 text-white" />
                </div>
                Additional Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    How did you hear about us?
                  </label>
                  <Input
                    value={formData.howDidYouHear}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        howDidYouHear: e.target.value,
                      })
                    }
                    placeholder="e.g., Google search, LinkedIn"
                    className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Specific Needs
                  </label>
                  <Input
                    value={formData.specificNeeds}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        specificNeeds: e.target.value,
                      })
                    }
                    placeholder="e.g., Employee wellness tracking"
                    className="rounded-xl border-gray-200 focus:border-blue-300 focus:ring-blue-200"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="px-6 py-3 rounded-xl border-gray-200 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleClick}
            disabled={isLoading}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating Lead...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create Lead
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewLeadModal;
